import React, { useEffect, useState, useMemo } from "react";
import Layout from "../../components/Layout/Layout";
import Loading from "../../components/Loading";
import { toast } from "react-toastify";
import {
  getTrashItems,
  restoreTrashItem,
  permanentDeleteTrashItem,
  emptyTrashApi,
} from "../../utils/api";
import {
  FaTrashAlt,
  FaTrashRestore,
  FaSearch,
  FaSyncAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimes,
} from "react-icons/fa";
import { formatDate } from "../../utils/formatData";

const CATEGORIES = [
  { id: "All", label: "All Items", icon: "🗑️" },
  { id: "Order", label: "Orders", icon: "🛍️" },
  { id: "Payment", label: "Payments", icon: "💰" },
  { id: "PurchaseOrder", label: "Purchase Orders", icon: "🛒" },
  { id: "Customer", label: "Customers", icon: "👥" },
  { id: "Invoice", label: "Invoices", icon: "🧾" },
  { id: "Product", label: "Products", icon: "🏷️" },
  { id: "Staff", label: "Staff", icon: "🧑‍💼" },
  { id: "CustomerGroup", label: "Groups", icon: "📁" },
  { id: "Contact", label: "Contacts", icon: "📇" },
];

const getTypeBadgeClass = (type) => {
  switch (type) {
    case "Order":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "Payment":
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "PurchaseOrder":
      return "bg-purple-100 text-purple-800 border-purple-200";
    case "Customer":
      return "bg-amber-100 text-amber-800 border-amber-200";
    case "Invoice":
      return "bg-indigo-100 text-indigo-800 border-indigo-200";
    case "Product":
      return "bg-pink-100 text-pink-800 border-pink-200";
    case "Staff":
      return "bg-teal-100 text-teal-800 border-teal-200";
    case "CustomerGroup":
      return "bg-cyan-100 text-cyan-800 border-cyan-200";
    case "Contact":
      return "bg-slate-100 text-slate-800 border-slate-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const Trash = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Modals for confirmation
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    type: "", // 'restore' | 'permanent' | 'empty'
    item: null,
  });

  const fetchTrash = async () => {
    setLoading(true);
    try {
      const [status, res] = await getTrashItems(activeTab, searchTerm);
      if (status && res?.data) {
        setItems(res.data);
      } else {
        toast.error(res?.message || "Failed to load trash items");
        setItems([]);
      }
    } catch (err) {
      toast.error(err?.message || "Error loading trash");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchTrash();
  };

  const handleRestore = async (item) => {
    setActionLoading(true);
    try {
      const [status, res] = await restoreTrashItem(item.type, item._id);
      if (status) {
        toast.success(res?.message || `${item.type} restored successfully!`);
        setItems((prev) => prev.filter((i) => i._id !== item._id));
        window.dispatchEvent(new Event("plabel-data-updated"));
        localStorage.setItem("plabel_last_sync", Date.now().toString());
      } else {
        toast.error(res || "Failed to restore item");
      }
    } catch (err) {
      toast.error(err?.message || "Error restoring item");
    } finally {
      setActionLoading(false);
      setConfirmModal({ open: false, type: "", item: null });
    }
  };

  const handlePermanentDelete = async (item) => {
    setActionLoading(true);
    try {
      const [status, res] = await permanentDeleteTrashItem(item.type, item._id);
      if (status) {
        toast.success(res?.message || `${item.type} permanently deleted!`);
        setItems((prev) => prev.filter((i) => i._id !== item._id));
        window.dispatchEvent(new Event("plabel-data-updated"));
        localStorage.setItem("plabel_last_sync", Date.now().toString());
      } else {
        toast.error(res || "Failed to delete item permanently");
      }
    } catch (err) {
      toast.error(err?.message || "Error deleting item");
    } finally {
      setActionLoading(false);
      setConfirmModal({ open: false, type: "", item: null });
    }
  };

  const handleEmptyTrash = async () => {
    setActionLoading(true);
    try {
      const [status, res] = await emptyTrashApi(activeTab);
      if (status) {
        toast.success(res?.message || "Trash emptied successfully!");
        fetchTrash();
        window.dispatchEvent(new Event("plabel-data-updated"));
        localStorage.setItem("plabel_last_sync", Date.now().toString());
      } else {
        toast.error(res || "Failed to empty trash");
      }
    } catch (err) {
      toast.error(err?.message || "Error emptying trash");
    } finally {
      setActionLoading(false);
      setConfirmModal({ open: false, type: "", item: null });
    }
  };

  // Filter items in memory by searchTerm if already fetched
  const filteredItems = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === "") return items;
    const q = searchTerm.toLowerCase().trim();
    return items.filter(
      (item) =>
        (item.title && item.title.toLowerCase().includes(q)) ||
        (item.subtitle && item.subtitle.toLowerCase().includes(q)) ||
        (item.customerName && item.customerName.toLowerCase().includes(q)) ||
        (item.type && item.type.toLowerCase().includes(q)) ||
        (item.deletedByName && item.deletedByName.toLowerCase().includes(q))
    );
  }, [items, searchTerm]);

  // Tab counts
  const categoryCounts = useMemo(() => {
    const counts = { All: items.length };
    items.forEach((item) => {
      counts[item.type] = (counts[item.type] || 0) + 1;
    });
    return counts;
  }, [items]);

  return (
    <Layout title="Trash & Restore Bin" reload={fetchTrash}>
      <div className="max-w-7xl mx-auto space-y-6 pb-12">
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white rounded-2xl p-6 shadow-xl border border-slate-700/50 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <span className="text-3xl p-2 bg-slate-800 rounded-xl border border-slate-700 shadow-inner">
                🗑️
              </span>
              <div>
                <h1 className="text-2xl font-black tracking-tight text-white flex items-center gap-2">
                  Trash & Recycle Bin
                </h1>
                <p className="text-xs font-medium text-slate-400">
                  Recover soft-deleted orders, payments, customers, and invoices or purge them permanently.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={fetchTrash}
              disabled={loading || actionLoading}
              className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-xl text-xs font-semibold text-slate-200 transition-all shadow-sm flex items-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} /> Refresh
            </button>

            {items.length > 0 && (
              <button
                onClick={() => setConfirmModal({ open: true, type: "empty", item: null })}
                disabled={actionLoading}
                className="px-4 py-2.5 bg-red-600/90 hover:bg-red-600 border border-red-500 rounded-xl text-xs font-bold text-white transition-all shadow-md shadow-red-900/30 flex items-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
              >
                <FaTrashAlt /> Empty {activeTab === "All" ? "All Trash" : `${activeTab} Trash`}
              </button>
            )}
          </div>
        </div>

        {/* Category Tabs */}
        <div className="bg-white rounded-2xl p-2 shadow-sm border border-slate-200 overflow-x-auto">
          <div className="flex items-center gap-1.5 min-w-max">
            {CATEGORIES.map((cat) => {
              const isActive = activeTab === cat.id;
              const count = categoryCounts[cat.id] ?? 0;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                    isActive
                      ? "bg-slate-900 text-white shadow-md shadow-slate-900/20"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  <span>{cat.icon}</span>
                  <span>{cat.label}</span>
                  {count > 0 && (
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-black ${
                        isActive
                          ? "bg-emerald-500 text-white"
                          : "bg-slate-200 text-slate-700"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <form onSubmit={handleSearchSubmit} className="relative w-full sm:w-96">
            <input
              type="text"
              placeholder="Search by customer, title, amount, product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-xs font-medium bg-slate-50 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-all placeholder:text-slate-400"
            />
            <FaSearch className="absolute left-3.5 top-3 text-slate-400 text-xs" />
          </form>

          <div className="text-xs font-semibold text-slate-500">
            Showing <span className="text-slate-900 font-bold">{filteredItems.length}</span> trashed item(s)
          </div>
        </div>

        {/* Content Table / Cards */}
        {loading ? (
          <div className="py-20 flex justify-center">
            <Loading />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-200 shadow-sm space-y-4">
            <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center text-3xl mx-auto shadow-inner">
              <FaCheckCircle />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-slate-800">
                {searchTerm ? "No matching trashed items found" : "Trash is Clean!"}
              </h3>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                {searchTerm
                  ? "Try adjusting your search criteria or switch category tabs."
                  : `There are no deleted items in ${activeTab === "All" ? "the trash bin" : activeTab}.`}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                    <th className="py-3.5 px-4">Type</th>
                    <th className="py-3.5 px-4">Title / Item</th>
                    <th className="py-3.5 px-4">Party / Customer</th>
                    <th className="py-3.5 px-4 text-right">Amount / Details</th>
                    <th className="py-3.5 px-4">Date Created</th>
                    <th className="py-3.5 px-4">Deleted At</th>
                    <th className="py-3.5 px-4">Deleted By</th>
                    <th className="py-3.5 px-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {filteredItems.map((item) => (
                    <tr
                      key={item._id}
                      className="hover:bg-slate-50/80 transition-colors group"
                    >
                      {/* Type Badge */}
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold border ${getTypeBadgeClass(
                            item.type
                          )}`}
                        >
                          {item.type}
                        </span>
                      </td>

                      {/* Title / Item Info */}
                      <td className="py-3 px-4">
                        <div className="font-bold text-slate-800">{item.title}</div>
                        {item.subtitle && (
                          <div className="text-[11px] text-slate-500">{item.subtitle}</div>
                        )}
                      </td>

                      {/* Customer / Party */}
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-700">
                          {item.customerName || "-"}
                        </div>
                      </td>

                      {/* Amount / Qty / Details */}
                      <td className="py-3 px-4 text-right">
                        {item.amount > 0 ? (
                          <div className="font-extrabold text-slate-900">
                            ₹{Number(item.amount).toLocaleString("en-IN")}
                          </div>
                        ) : null}
                        {item.quantity > 0 && (
                          <div className="text-[11px] text-slate-500 font-medium">
                            {Number(item.quantity).toLocaleString("en-IN")} pcs @ ₹{item.rate}
                          </div>
                        )}
                        {!item.amount && !item.quantity && (
                          <div className="text-slate-400 font-mono">-</div>
                        )}
                      </td>

                      {/* Date */}
                      <td className="py-3 px-4 text-slate-600 whitespace-nowrap">
                        {item.date ? formatDate(item.date) : "-"}
                      </td>

                      {/* Deleted At */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className="font-medium text-red-600">
                          {item.deletedAt ? new Date(item.deletedAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }) : "-"}
                        </div>
                      </td>

                      {/* Deleted By */}
                      <td className="py-3 px-4 text-slate-600 font-medium">
                        {item.deletedByName || "Admin"}
                      </td>

                      {/* Action Buttons */}
                      <td className="py-3 px-4 text-center whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() =>
                              setConfirmModal({
                                open: true,
                                type: "restore",
                                item: item,
                              })
                            }
                            disabled={actionLoading}
                            title="Restore this item"
                            className="px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95"
                          >
                            <FaTrashRestore className="text-xs" /> Restore
                          </button>

                          <button
                            onClick={() =>
                              setConfirmModal({
                                open: true,
                                type: "permanent",
                                item: item,
                              })
                            }
                            disabled={actionLoading}
                            title="Permanently delete from database"
                            className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-xs font-bold transition-all cursor-pointer shadow-xs active:scale-95"
                          >
                            <FaTrashAlt className="text-xs" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmModal.open && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-200 space-y-5 animate-in zoom-in-95 duration-200">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl shadow-inner ${
                      confirmModal.type === "restore"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-red-100 text-red-600"
                    }`}
                  >
                    {confirmModal.type === "restore" ? (
                      <FaTrashRestore />
                    ) : (
                      <FaExclamationTriangle />
                    )}
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900">
                      {confirmModal.type === "restore"
                        ? "Confirm Restore"
                        : confirmModal.type === "permanent"
                        ? "Delete Permanently"
                        : "Empty Entire Trash"}
                    </h3>
                    <p className="text-xs text-slate-500 font-medium">
                      {confirmModal.type === "restore"
                        ? "Item will be restored back to active view."
                        : confirmModal.type === "permanent"
                        ? "This action CANNOT be undone."
                        : "All deleted items in this category will be permanently erased."}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setConfirmModal({ open: false, type: "", item: null })}
                  className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition cursor-pointer"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Item Preview */}
              {confirmModal.item && (
                <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500 font-medium">Type:</span>
                    <span className="font-bold text-slate-800">{confirmModal.item.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 font-medium">Title:</span>
                    <span className="font-bold text-slate-800">{confirmModal.item.title}</span>
                  </div>
                  {confirmModal.item.customerName && confirmModal.item.customerName !== "-" && (
                    <div className="flex justify-between">
                      <span className="text-slate-500 font-medium">Customer:</span>
                      <span className="font-bold text-slate-800">{confirmModal.item.customerName}</span>
                    </div>
                  )}
                  {confirmModal.item.amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500 font-medium">Amount:</span>
                      <span className="font-extrabold text-emerald-700">
                        ₹{Number(confirmModal.item.amount).toLocaleString("en-IN")}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
                <button
                  onClick={() => setConfirmModal({ open: false, type: "", item: null })}
                  disabled={actionLoading}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer"
                >
                  Cancel
                </button>

                {confirmModal.type === "restore" ? (
                  <button
                    onClick={() => handleRestore(confirmModal.item)}
                    disabled={actionLoading}
                    className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-extrabold shadow-md shadow-emerald-700/20 transition-all flex items-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
                  >
                    <FaTrashRestore /> {actionLoading ? "Restoring..." : "Yes, Restore"}
                  </button>
                ) : confirmModal.type === "permanent" ? (
                  <button
                    onClick={() => handlePermanentDelete(confirmModal.item)}
                    disabled={actionLoading}
                    className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-extrabold shadow-md shadow-red-700/20 transition-all flex items-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
                  >
                    <FaTrashAlt /> {actionLoading ? "Deleting..." : "Permanently Delete"}
                  </button>
                ) : (
                  <button
                    onClick={handleEmptyTrash}
                    disabled={actionLoading}
                    className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-extrabold shadow-md shadow-red-700/20 transition-all flex items-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
                  >
                    <FaTrashAlt /> {actionLoading ? "Emptying..." : "Yes, Empty Trash"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Trash;
